import { FC, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, Divider, useMediaQuery } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import ReactJson from 'react-json-view';
import type { Item } from '../../../../types/item';
import { PropertyList } from '../../../../components/property-list';
import { PropertyListItem } from '../../../../components/property-list-item';
import { useAuth } from '../../../../hooks/use-auth';
import { useDialog } from '../../../../hooks/use-dialog';
import { ItemEditDetailsDialog } from './item-edit-details-dialog';
import {
  getAssetConfig,
  dynamicFields,
  fieldLabel,
  formatFieldValue,
  getDetail,
  getIdentifierField,
  getTitleField,
  itemOnlyFields,
} from '../../../../config/asset-config';

interface ItemDetailsCardProps {
  item: Item;
  location?: 'details' | 'other';
}

export const ItemDetailsCard: FC<ItemDetailsCardProps> = (props) => {
  const { item, location, ...other } = props;
  const { tenant } = useAuth();

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const align = mdDown ? 'vertical' : 'horizontal';

  const [open, handleOpen, handleClose] = useDialog();

  const showMetadata = useMemo(() => Object.keys(item.publicMetadata).length > 0, [item]);

  const config = useMemo(() => getAssetConfig(tenant), [tenant]);
  const titleField = useMemo(() => getTitleField(config), [config]);
  // Identity trio, matching the meta line under the item title: the operator's serial (the
  // identifier field), the fingerprint number, and the UID.
  const identifierField = useMemo(() => getIdentifierField(config), [config]);
  const fingerprintNumber = (item.publicMetadata as { skuSequence?: number } | undefined)
    ?.skuSequence;
  // With a SKU, the title field (e.g. player) is SKU-level catalog data, shown by the separate
  // SKU Details card. Only a SKU-less item carries it on the item itself.
  const titleFieldValue =
    titleField && !item.sku ? getDetail(item, titleField.id) : undefined;

  // The item's own field values (from publicMetadata.details): item-only fields when there's a
  // SKU (catalog fields live on the SKU), or all dynamic fields for a SKU-less item.
  const detailFields = useMemo(
    () =>
      (item.sku ? itemOnlyFields(config) : dynamicFields(config))
        // The identifier field has its own Serial Number row above.
        .filter((f) => f.id !== identifierField?.id)
        .filter((f) => {
          const v = getDetail(item, f.id);
          return v !== undefined && v !== null && v !== '';
        }),
    [config, item, identifierField],
  );

  return (
    <>
      <Card variant="outlined" {...other}>
        <CardHeader
          title="Item Details"
          action={
            location === 'other' ? (
              <Button
                color="primary"
                component={RouterLink}
                to={`/tenants/${tenant?.id}/items/${item.id}`}
                variant="text"
              >
                Show
              </Button>
            ) : (
              tenant?.isActive && (
                <Button color="primary" onClick={handleOpen} variant="text">
                  Edit
                </Button>
              )
            )
          }
        />
        <Divider />
        <PropertyList>
          <PropertyListItem align={align} label="Title" value={item.title} />
          {identifierField && (
            <PropertyListItem
              align={align}
              label="Serial Number (SN)"
              value={getDetail(item, identifierField.id) || '—'}
            />
          )}
          {fingerprintNumber != null && (
            <PropertyListItem
              align={align}
              label="Fingerprinting Number (FN)"
              value={String(fingerprintNumber).padStart(3, '0')}
            />
          )}
          <PropertyListItem align={align} label="Unique ID (UID)" value={item.uniqueId} />
          {titleField && !item.sku && titleFieldValue && (
            <PropertyListItem
              align={align}
              label={fieldLabel(titleField)}
              value={formatFieldValue(titleField, titleFieldValue)}
            />
          )}
          {detailFields.map((field) => (
            <PropertyListItem
              key={field.id}
              align={align}
              label={fieldLabel(field)}
              value={formatFieldValue(field, getDetail(item, field.id))}
            />
          ))}
          {item.subtitle && (
            <PropertyListItem align={align} label="Subtitle" value={item.subtitle} />
          )}
          {item.externalId && (
            <PropertyListItem align={align} label="External ID" value={item.externalId} />
          )}

          {item.protectedBy && (
            <PropertyListItem align={align} label="Protected by" value={item.protectedBy?.email} />
          )}
          {item.protectedAt && (
            <PropertyListItem
              align={align}
              label="Protected at"
              value={format(new Date(item.protectedAt), 'h:mmaaa MM/dd/yyyy')}
            />
          )}
          {item.registeredBy && (
            <PropertyListItem
              align={align}
              label="Registered by"
              value={item.registeredBy?.email}
            />
          )}
          {item.registeredAt && (
            <PropertyListItem
              align={align}
              label="Registered at"
              value={format(new Date(item.registeredAt), 'h:mmaaa MM/dd/yyyy')}
            />
          )}
          {showMetadata && <PropertyListItem align={align} label="Metadata" />}
        </PropertyList>
        {showMetadata && (
          <CardContent
            sx={{
              pt: 0,
            }}
          >
            <ReactJson src={item.publicMetadata} name={false} collapsed />
          </CardContent>
        )}
      </Card>
      {open && <ItemEditDetailsDialog open={open} onClose={handleClose} item={item} />}
    </>
  );
};

ItemDetailsCard.propTypes = {
  // @ts-ignore
  item: PropTypes.object.isRequired,
  location: PropTypes.oneOf(['details', 'other']),
};
